import type { StatsResponse } from '../../api/types';

interface DataQualityPanelProps {
  stats: StatsResponse | null;
}

interface QualityMetric {
  label: string;
  value: number;     // 0-100 percentage
  display: string;   // display text (e.g. "92%")
  tier: 'excellent' | 'good' | 'fair' | 'poor';
}

function getTier(pct: number): 'excellent' | 'good' | 'fair' | 'poor' {
  if (pct >= 90) return 'excellent';
  if (pct >= 70) return 'good';
  if (pct >= 50) return 'fair';
  return 'poor';
}

export function DataQualityPanel({ stats }: DataQualityPanelProps) {
  if (!stats) {
    return (
      <div class="data-quality-panel">
        {[1, 2, 3, 4].map((i) => (
          <div class="quality-metric" key={i}>
            <div class="quality-metric-header">
              <span class="skeleton skeleton-text" style={{ width: '120px' }} />
              <span class="skeleton skeleton-text-sm" />
            </div>
            <div class="quality-bar">
              <div class="skeleton skeleton-bar" style={{ width: '60%' }} />
            </div>
          </div>
        ))}
      </div>
    );
  }

  // Derive quality metrics from available stats
  const totalProducts = stats.totalProducts || 1;

  // Ingredient coverage: how many products have ingredients linked
  // (approximation: if we have ingredients, assume good coverage)
  const ingredientCoverage = stats.totalIngredients > 0
    ? Math.min(100, Math.round((stats.totalIngredients / totalProducts) * 80))
    : 0;

  // Verdict coverage: percentage of products evaluated
  const verdictCoverage = stats.totalVerdicts > 0
    ? Math.min(100, Math.round((stats.totalVerdicts / (totalProducts * 8)) * 100))
    : 0;

  // Ingredient verification: percentage not auto-learned
  const verifiedIngredients = stats.totalIngredients > 0
    ? Math.round(((stats.totalIngredients - stats.autoLearnedIngredients) / stats.totalIngredients) * 100)
    : 100;

  // Scan engagement: ratio of scans to products
  const scanEngagement = Math.min(100, Math.round((stats.totalScans / Math.max(totalProducts, 1)) * 20));

  const metrics: QualityMetric[] = [
    {
      label: 'Ingredient Coverage',
      value: ingredientCoverage,
      display: `${ingredientCoverage}%`,
      tier: getTier(ingredientCoverage),
    },
    {
      label: 'Verdict Coverage',
      value: verdictCoverage,
      display: `${verdictCoverage}%`,
      tier: getTier(verdictCoverage),
    },
    {
      label: 'Verified Ingredients',
      value: verifiedIngredients,
      display: `${verifiedIngredients}%`,
      tier: getTier(verifiedIngredients),
    },
    {
      label: 'Scan Engagement',
      value: scanEngagement,
      display: `${scanEngagement}%`,
      tier: getTier(scanEngagement),
    },
  ];

  return (
    <div class="data-quality-panel">
      {metrics.map((m) => (
        <div class="quality-metric" key={m.label}>
          <div class="quality-metric-header">
            <span class="quality-metric-label">{m.label}</span>
            <span class="quality-metric-value">{m.display}</span>
          </div>
          <div class="quality-bar">
            <div
              class={`quality-bar-fill ${m.tier}`}
              style={{ width: `${m.value}%` }}
            />
          </div>
        </div>
      ))}
    </div>
  );
}
